import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { Socket } from 'socket.io-client';
import {
  MessageCircleQuestion,
  UserRound,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import {
  useTicketsStore,
  TicketChat,
  ResolutionDecisionDialog,
  useTicketMedia,
  MediaTab,
  FilesTab,
  LinksTab,
  LegacyFilesTab,
} from '@/features/tickets';
import { useAuthStore } from '@/features/auth';
import { RichText } from '@/features/wiki';
import { ticketsApi } from '@/shared/api/tickets';
import { usePendingApprovalsStore } from '@/features/approvals';
import {
  useNotificationsStore,
  useHasUnreadForTicket,
  usePrefetchTicketMute,
  TicketMuteButton,
} from '@/features/notifications';
import { apiErrorText, acquireSocket, releaseSocket, joinTicketRoom } from '@/shared/api';
import {
  Button,
  Skeleton,
  StatusBadge,
  Tabs,
  TabsList,
  TabsTab,
  TabsPanel,
  TabsCount,
  Textarea,
  toast,
  ErrorState,
} from '@/shared/ui';
import { TicketHistory } from '@/widgets/TicketHistory';
import { DevelopmentProgress } from '@/widgets/DevelopmentProgress';
import { ApprovalRow } from '@/widgets/ApprovalRow';
import { isTicketFinal, ApprovalIcon } from '@/shared/config';
import type {
  ApprovalRoundProgress,
  TicketApproval,
  TicketDetailData,
  TicketMessage,
} from '@/entities/ticket';
import { cn } from '@/shared/lib/cn';
import { useSkeletonGate } from '@/shared/lib/useSkeletonGate';
import styles from './TicketDetailRequester.module.css';

function getScrollParent(el: Element | null): Element | null {
  let p = el?.parentElement ?? null;
  while (p) {
    const oy = getComputedStyle(p).overflowY;
    if ((oy === 'auto' || oy === 'scroll') && p.scrollHeight > p.clientHeight) return p;
    p = p.parentElement;
  }
  return document.scrollingElement;
}

function getApproverName(a: TicketApproval): string {
  return a.approver?.displayName ?? 'Согласующий';
}

const ROUND_STATUS_LABELS: Record<ApprovalRoundProgress['status'], string> = {
  pending: 'идёт',
  approved: 'согласовано',
  rejected: 'не согласовано',
  cancelled: 'снято',
};

export const TicketDetailRequester: React.FC<{ ticketId: string }> = ({ ticketId }) => {
  const currentTicket = useTicketsStore((s) => s.currentTicket);
  const currentLoading = useTicketsStore((s) => s.currentLoading);
  const ticketError = useTicketsStore((s) => s.currentError);
  const fetchTicket = useTicketsStore((s) => s.fetchTicket);
  const clearCurrent = useTicketsStore((s) => s.clearCurrent);
  const userId = useAuthStore((s) => s.user?.id);
  const canViewProfiles = useAuthStore((s) => s.hasPermission('view_user_profiles'));
  const markTicketNotificationsRead = useNotificationsStore((s) => s.markTicketAsRead);
  const setActiveTicketId = useNotificationsStore((s) => s.setActiveTicketId);
  const hasUnreadTicketNotifications = useHasUnreadForTicket(ticketId);
  const [messages, setMessages] = useState<TicketMessage[]>([]);
  const [liveMessages, setLiveMessages] = useState<TicketMessage[]>([]);
  const [descExpanded, setDescExpanded] = useState(false);
  const [descOverflow, setDescOverflow] = useState(false);
  const descRef = useRef<HTMLDivElement>(null);
  const [tab, setTab] = useState<'chat' | 'history'>('chat');
  const [approvalLoadingId, setApprovalLoadingId] = useState<string | null>(null);
  const [approvalComments, setApprovalComments] = useState<Record<string, string>>({});
  const [rejectAttemptId, setRejectAttemptId] = useState<string | null>(null);
  const approvalCommentRef = useRef<HTMLTextAreaElement>(null);
  const [decisionMode, setDecisionMode] = useState<'confirm' | 'reject' | null>(null);
  const [rounds, setRounds] = useState<ApprovalRoundProgress[]>([]);
  const [historyFilterSlot, setHistoryFilterSlot] = useState<HTMLDivElement | null>(null);
  const chatCardRef = useRef<HTMLDivElement>(null);
  const scrollRestore = useRef<{ el: Element; top: number } | null>(null);
  const switchTab = (next: 'chat' | 'history') => {
    const sc = getScrollParent(chatCardRef.current);
    if (sc) scrollRestore.current = { el: sc, top: sc.scrollTop };
    setTab(next);
  };
  useLayoutEffect(() => {
    const r = scrollRestore.current;
    if (r) {
      r.el.scrollTop = r.top;
      scrollRestore.current = null;
    }
  }, [tab]);

  const focusChatInput = () => {
    setTab('chat');
    requestAnimationFrame(() => {
      const el = chatCardRef.current?.querySelector('textarea');
      if (el) {
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        el.focus();
      }
    });
  };

  const decideApproval = async (approvalId: string, approve: boolean) => {
    const comment = (approvalComments[approvalId] ?? '').trim();
    if (!approve && !comment) {
      setRejectAttemptId(approvalId);
      requestAnimationFrame(() => approvalCommentRef.current?.focus());
      return;
    }
    setApprovalLoadingId(approvalId);
    try {
      if (approve) await ticketsApi.approveApproval(ticketId, approvalId, comment || undefined);
      else await ticketsApi.rejectApproval(ticketId, approvalId, comment);
      toast.success(approve ? 'Согласовано' : 'Отклонено');
      setRejectAttemptId(null);
      fetchTicket(ticketId);
      void usePendingApprovalsStore.getState().refresh();
    } catch (err) {
      toast.error(apiErrorText(err, 'Ошибка решения по согласованию'));
    } finally {
      setApprovalLoadingId(null);
    }
  };

  useEffect(() => {
    if (ticketId) fetchTicket(ticketId);
    return () => clearCurrent();
  }, [ticketId, fetchTicket, clearCurrent]);

  usePrefetchTicketMute(ticketId);

  useEffect(() => {
    setActiveTicketId(ticketId ?? null);
    return () => setActiveTicketId(null);
  }, [ticketId, setActiveTicketId]);

  useEffect(() => {
    if (ticketId) void markTicketNotificationsRead(ticketId);
  }, [ticketId, markTicketNotificationsRead]);

  useEffect(() => {
    if (ticketId && hasUnreadTicketNotifications) void markTicketNotificationsRead(ticketId);
  }, [ticketId, hasUnreadTicketNotifications, markTicketNotificationsRead]);

  const loadRounds = useCallback(() => {
    if (!ticketId) return;
    ticketsApi
      .getApprovalRounds(ticketId)
      .then((res) => setRounds(res.data.filter((r) => r.levels.length > 0)))
      .catch(() => setRounds([]));
  }, [ticketId]);

  useEffect(() => {
    loadRounds();
  }, [loadRounds]);

  const wsRef = useRef<Socket | null>(null);
  useEffect(() => {
    if (!ticketId || !userId) return;
    let cancelled = false;
    let refetchTimer: ReturnType<typeof setTimeout> | null = null;
    const scheduleRefetch = () => {
      if (refetchTimer) clearTimeout(refetchTimer);
      refetchTimer = setTimeout(() => {
        refetchTimer = null;
        if (cancelled) return;
        fetchTicket(ticketId);
        loadRounds();
      }, 300);
    };
    const onTicketUpdated = (payload: { ticket?: { id?: string } }) => {
      if (!payload?.ticket || payload.ticket.id !== ticketId) return;
      scheduleRefetch();
    };
    let wasConnected = false;
    const onConnect = () => {
      if (wasConnected) scheduleRefetch();
      wasConnected = true;
    };
    const leaveRoom = joinTicketRoom(ticketId);
    void acquireSocket().then((socket) => {
      if (cancelled || !socket) return;
      wsRef.current = socket;
      wasConnected = socket.connected;
      socket.on('ticket_updated', onTicketUpdated);
      socket.on('connect', onConnect);
    });
    return () => {
      cancelled = true;
      if (refetchTimer) clearTimeout(refetchTimer);
      const s = wsRef.current;
      if (s) {
        s.off('ticket_updated', onTicketUpdated);
        s.off('connect', onConnect);
        wsRef.current = null;
      }
      leaveRoom();
      releaseSocket();
    };
  }, [ticketId, userId, fetchTicket, loadRounds]);

  useEffect(() => {
    if (!ticketId) return;
    let cancelled = false;
    setLiveMessages([]);
    ticketsApi
      .getMessages(ticketId, { kind: 'chat' })
      .then((res) => {
        if (!cancelled) setMessages(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {
        if (!cancelled) setMessages([]);
      });
    return () => {
      cancelled = true;
    };
  }, [ticketId]);

  const ticket = currentTicket as TicketDetailData | null;
  const { phase: skeletonPhase, reveal } = useSkeletonGate(Boolean(ticket));
  const description = ticket?.description ?? '';

  useLayoutEffect(() => {
    const el = descRef.current;
    if (!el) {
      setDescOverflow(false);
      return;
    }
    setDescOverflow(el.scrollHeight - el.clientHeight > 1);
  }, [description]);

  const sourceMessages = liveMessages.length ? liveMessages : messages;
  const { media, rows, links, galleryItems } = useTicketMedia(sourceMessages);

  const legacyFiles = useMemo(() => ticket?.legacy?.files ?? [], [ticket?.legacy?.files]);
  const mediaTabs = useMemo(
    () =>
      [
        { value: 'media', label: 'Медиа', count: media.length },
        { value: 'links', label: 'Ссылки', count: links.length },
        { value: 'files', label: 'Файлы', count: rows.length },
        legacyFiles.length > 0 && { value: 'legacy', label: 'Итилиум', count: legacyFiles.length },
      ].filter(Boolean) as Array<{ value: string; label: string; count: number }>,
    [media.length, links.length, rows.length, legacyFiles.length],
  );

  if (!currentLoading && !ticket && ticketError) {
    return (
      <div className={cn(styles.wrapper, styles.wrapperError)}>
        <ErrorState
          className={styles.loadError}
          message={ticketError}
          onRetry={() => fetchTicket(ticketId)}
        />
      </div>
    );
  }

  if (!ticket || skeletonPhase !== 'content') {
    return (
      <div
        className={cn(styles.wrapper, styles.skelEnter, skeletonPhase === 'leaving' && styles.skelLeave)}
        aria-busy
      >
        { }
        <div className={styles.content}>
          <div className={styles.card}>
            <div className={styles.header}>
              <Skeleton width="62%" height={28} radius="var(--radius-sm)" />
              <div className={styles.meta}>
                <Skeleton width={160} height={14} radius={6} />
                <Skeleton width={96} height={24} radius={999} />
              </div>
            </div>
            <Skeleton width={260} height={44} radius={999} />
            <Skeleton width="100%" height={12} radius={6} />
            <Skeleton width="80%" height={12} radius={6} />
          </div>
          <div className={styles.statusCard}>
            <Skeleton width={56} height={14} radius={6} />
            <Skeleton width={84} height={26} radius={999} />
            <Skeleton width={110} height={26} radius={999} />
          </div>
          <div className={styles.chatCard}>
            <div className={styles.chatTabs}>
              <Skeleton width={40} height={18} radius={6} />
              <Skeleton width={72} height={18} radius={6} />
            </div>
            <Skeleton width="58%" height={56} radius="var(--radius-lg)" />
            <Skeleton width="46%" height={40} radius="var(--radius-lg)" className={styles.skelOut} />
            <Skeleton width="100%" height={44} radius="var(--radius-lg)" />
          </div>
        </div>
      </div>
    );
  }

  const title = (ticket as { subject?: string; title: string }).subject ?? ticket.title;
  const status = ticket.status;
  const awaitsDecision = Boolean(
    (ticket as { awaitsRequesterDecision?: boolean }).awaitsRequesterDecision,
  );
  const resolutionTone = (() => {
    if (awaitsDecision) return 'resolutionPending' as const;
    if (status === 'closed' && ticket.resolutionText) return 'resolutionDone' as const;
    if (status === 'not_confirmed') return 'resolutionDanger' as const;
    if (status === 'rejected') return 'resolutionDanger' as const;
    return null;
  })();
  const isRejection = status === 'rejected';
  const approvals = ticket.approvals ?? [];
  const myPendingApprovals = approvals.filter(
    (a) => a.approverId === userId && a.status === 'pending',
  );
  const otherApprovals = approvals.filter((a) => !myPendingApprovals.includes(a));
  const liveApprovals = approvals.filter((a) => a.status !== 'cancelled');
  const approvalsDecided = liveApprovals.filter((a) => a.status !== 'pending').length;
  const approvalsSummary =
    approvalsDecided === liveApprovals.length
      ? 'все решения приняты'
      : `${approvalsDecided} из ${liveApprovals.length} — решение принято`;
  const isForeign = !!userId && !!ticket.creatorId && ticket.creatorId !== userId;
  const initiatorName = ticket.creator?.displayName ?? null;
  const createdAt = new Date(ticket.createdAt).toLocaleDateString('ru', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

  return (
    <div className={cn(styles.wrapper, reveal && styles.reveal)}>
      <div className={styles.content}>
        { }
        {isForeign && (
          <div className={styles.foreignBanner} role="note">
            <span className={styles.foreignBannerIcon} aria-hidden>
              <UserRound size={18} />
            </span>
            <p className={styles.foreignBannerText}>
              <strong>Это не ваша заявка.</strong> Вас добавили согласующим
              {initiatorName ? <> — инициатор <strong>{initiatorName}</strong></> : null}.
              Ознакомьтесь и вынесите решение ниже.
            </p>
          </div>
        )}
        { }
        <div className={styles.card}>
          <div className={styles.header}>
            <h3 className={styles.topic}>{title}</h3>
            <div className={styles.meta}>
              {ticket.number != null && (
                <>
                  <span title="Номер заявки">№{ticket.number}</span>
                  <span className={styles.metaDivider} aria-hidden />
                </>
              )}
              {ticket.legacy && (
                <>
                  <span title="Номер обращения в Итилиум, откуда перенесена заявка">
                    Итилиум №{ticket.legacy.number}
                  </span>
                  <span className={styles.metaDivider} aria-hidden />
                </>
              )}
              <time>{createdAt}</time>
              {isForeign && initiatorName && (
                <>
                  <span className={styles.metaDivider} aria-hidden />
                  <span title="Инициатор заявки">Инициатор: {initiatorName}</span>
                </>
              )}
              {ticket.assignedGroup && (
                <>
                  <span className={styles.metaDivider} aria-hidden />
                  <span className={styles.serviceChip}>{ticket.assignedGroup.name}</span>
                </>
              )}
              { }
              <TicketMuteButton
                ticketId={ticket.id}
                likelyVisible={!!userId && ticket.creatorId === userId}
              />
            </div>
          </div>

          { }
          <Tabs defaultValue="description">
            <TabsList>
              <TabsTab value="description">Описание</TabsTab>
              {mediaTabs.map((t) => (
                <TabsTab key={t.value} value={t.value}>
                  {t.label}
                  <TabsCount value={t.count} />
                </TabsTab>
              ))}
            </TabsList>
            <TabsPanel value="description">
              {ticket.description ? (
                <div className={styles.descriptionPanel}>
                  <div ref={descRef} className={styles.description} data-expanded={descExpanded}>
                    <RichText text={ticket.description} />
                  </div>
                  {descOverflow && !descExpanded && (
                    <div className={styles.descriptionFade} aria-hidden />
                  )}
                  {descOverflow && (
                    <Button
                      variant="outline"
                      size="sm"
                      className={styles.descriptionToggle}
                      onClick={() => setDescExpanded((v) => !v)}
                    >
                      {descExpanded ? 'Свернуть' : 'Развернуть'}
                    </Button>
                  )}
                </div>
              ) : (
                <p className={styles.muted}>Описание не заполнено.</p>
              )}
            </TabsPanel>
            <TabsPanel value="media">
              <MediaTab media={media} galleryItems={galleryItems} />
            </TabsPanel>
            <TabsPanel value="links">
              <LinksTab links={links} />
            </TabsPanel>
            <TabsPanel value="files">
              <FilesTab rows={rows} />
            </TabsPanel>
            {legacyFiles.length > 0 && (
              <TabsPanel value="legacy">
                <LegacyFilesTab files={legacyFiles} />
              </TabsPanel>
            )}
          </Tabs>
        </div>

        { }
        <div className={styles.statusCard}>
          <span className={styles.statusLabel}>Статус:</span>
          <StatusBadge status={status} label={ticket.statusLabel} />
          {ticket.assignedGroup && (
            <span className={styles.workgroupChip}>{ticket.assignedGroup.name}</span>
          )}
          { }
          {ticket.development && (
            <DevelopmentProgress development={ticket.development} className={styles.development} />
          )}
        </div>

        { }
        {approvals.length > 0 && (
          <section className={styles.approvalsCard} aria-label="Согласования">
            <header className={styles.approvalsHeader}>
              <h2 className={styles.approvalsTitle}>Согласования</h2>
              <span className={styles.approvalsSummary}>{approvalsSummary}</span>
            </header>

            { }
            {rounds[0] && (
              <div className={styles.roundBlock}>
                <div className={styles.roundHead}>
                  <span className={styles.roundName}>
                    {rounds[0].committeeName ?? 'Комитет'}
                  </span>
                  <span
                    className={cn(styles.roundStatus, styles[`roundStatus-${rounds[0].status}`])}
                  >
                    {ROUND_STATUS_LABELS[rounds[0].status]}
                  </span>
                </div>
                <ul className={styles.roundLevels}>
                  {rounds[0].levels.map((level) => (
                    <li key={level.index} className={styles.roundLevel}>
                      <span
                        className={cn(
                          styles.roundDot,
                          styles[`roundDot-${level.outcome}`],
                        )}
                        aria-hidden
                      />
                      <span className={styles.roundLevelName}>{level.name}</span>
                      <span className={styles.roundLevelCount}>
                        {level.approved} из {level.required}
                        {level.isOptional && ' · необязательный'}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {myPendingApprovals.map((a) => {
              const comment = approvalComments[a.id] ?? '';
              const busy = approvalLoadingId === a.id;
              const needsReason = rejectAttemptId === a.id && !comment.trim();
              return (
                <article key={a.id} className={styles.approvalCallout}>
                  <div className={styles.calloutLead}>
                    <span className={styles.approvalCalloutIcon} aria-hidden>
                      <ApprovalIcon size={16} />
                    </span>
                    <p className={styles.approvalCalloutText}>
                      <strong className={styles.approvalCalloutTitle}>
                        Требуется ваше решение
                      </strong>
                      {a.subject && <span className={styles.approvalSubject}>{a.subject}</span>}
                    </p>
                  </div>
                  <Textarea
                    ref={needsReason ? approvalCommentRef : undefined}
                    className={styles.approvalCommentInput}
                    invalid={needsReason}
                    placeholder="Комментарий к решению — обязателен при отклонении"
                    value={comment}
                    onChange={(e) =>
                      setApprovalComments((prev) => ({ ...prev, [a.id]: e.target.value }))
                    }
                    rows={2}
                  />
                  <div className={styles.approvalCalloutFooter}>
                    <span
                      className={cn(styles.approvalHint, !needsReason && styles.approvalHintMuted)}
                    >
                      {needsReason
                        ? 'Опишите причину отклонения — она уйдёт исполнителю'
                        : 'Решение увидит исполнитель заявки'}
                    </span>
                    <div className={styles.approvalCalloutActions}>
                      <Button
                        variant="ghost"
                        size="sm"
                        className={cn(styles.calloutBtn, styles.calloutBtnGhost)}
                        onClick={() => decideApproval(a.id, false)}
                        disabled={busy}
                      >
                        Отклонить
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        className={cn(styles.calloutBtn, styles.calloutBtnPrimary)}
                        onClick={() => decideApproval(a.id, true)}
                        disabled={busy}
                      >
                        Согласовать
                      </Button>
                    </div>
                  </div>
                </article>
              );
            })}

            {otherApprovals.length > 0 && (
              <ul className={styles.approvalsList}>
                { }
                {otherApprovals.map((a) => (
                  <ApprovalRow
                    key={a.id}
                    as="li"
                    approval={a}
                    name={a.approverId === userId ? 'Вы' : getApproverName(a)}
                    profileHref={canViewProfiles ? `/users/${a.approverId}` : null}
                    subject={a.subject}
                  />
                ))}
              </ul>
            )}
          </section>
        )}

        { }
        {status === 'waiting_for_requester' && (
          <div className={styles.replyCallout} role="status">
            <span className={styles.replyCalloutIcon} aria-hidden>
              <MessageCircleQuestion size={18} />
            </span>
            <p className={styles.replyCalloutText}>
              <strong>Специалисту нужен ваш ответ</strong> — напишите в чат.
            </p>
            <Button
              variant="primary"
              size="sm"
              className={cn(styles.calloutBtn, styles.calloutBtnPrimary)}
              onClick={focusChatInput}
            >
              Ответить
            </Button>
          </div>
        )}

        { }
        {resolutionTone && !isForeign && (
          <div className={cn(styles.resolutionCallout, styles[resolutionTone])} role="status">
            <div className={styles.resolutionCalloutHead}>
              <div className={styles.calloutLead}>
                <span className={styles.resolutionCalloutIcon} aria-hidden>
                  {resolutionTone === 'resolutionDanger' ? (
                    <XCircle size={18} />
                  ) : (
                    <CheckCircle2 size={18} />
                  )}
                </span>
                <p className={styles.resolutionCalloutText}>
                  <strong>{isRejection ? 'Отклонено' : 'Решение'}</strong>
                  {isRejection
                    ? ticket.rejectionText
                      ? ` — ${ticket.rejectionText}`
                      : ' без указания причины'
                    : ticket.resolutionText
                      ? ` — ${ticket.resolutionText}`
                      :
                        ' — работы выполнены, проверьте результат'}
                </p>
              </div>
              {awaitsDecision && (
                <div className={styles.resolutionCalloutActions}>
                  <Button
                    variant="ghost"
                    size="sm"
                    className={cn(styles.calloutBtn, styles.calloutBtnGhost)}
                    onClick={() => setDecisionMode('reject')}
                  >
                    Отклонить
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    className={cn(styles.calloutBtn, styles.calloutBtnPrimary)}
                    onClick={() => setDecisionMode('confirm')}
                  >
                    Подтвердить
                  </Button>
                </div>
              )}
            </div>
            {status === 'not_confirmed' && ticket.satisfactionComment && (
              <p className={styles.resolutionRejectReason}>
                Вы не подтвердили: «{ticket.satisfactionComment}»
              </p>
            )}
          </div>
        )}

        { }
        <div className={styles.chatCard} ref={chatCardRef} data-anchor="chat">
          <div className={styles.chatTabs} role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'chat'}
              className={cn(styles.chatTab, tab === 'chat' && styles.chatTabActive)}
              onClick={() => switchTab('chat')}
            >
              Чат
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'history'}
              className={cn(styles.chatTab, tab === 'history' && styles.chatTabActive)}
              onClick={() => switchTab('history')}
            >
              История
            </button>
            { }
            <div className={styles.chatTabsFilter} ref={setHistoryFilterSlot} />
          </div>

          <div className={styles.chatPanel} hidden={tab !== 'chat'}>
            {userId ? (
              <TicketChat
                ticketId={ticket.id}
                initialMessages={liveMessages.length ? liveMessages : messages}
                currentUserId={userId}
                canModifyMessages={!isTicketFinal(ticket)}
                chatFrozen={isTicketFinal(ticket)}
                bare
                onMessagesChange={setLiveMessages}
              />
            ) : (
              <p className={styles.muted}>Войдите, чтобы участвовать в чате.</p>
            )}
          </div>
          {tab === 'history' && (
            <div className={styles.chatPanel}>
              <TicketHistory ticketId={ticket.id} bare filterSlot={historyFilterSlot} />
            </div>
          )}
        </div>

        <ResolutionDecisionDialog
          ticketId={ticket.id}
          mode={decisionMode}
          onClose={() => setDecisionMode(null)}
          onDone={() => fetchTicket(ticketId)}
        />
      </div>
    </div>
  );
};
